import cloneDeep from 'lodash/cloneDeep';
import {
  DEFALUT_PERMISSION_SECTION,
  DEFAULT_PERMISSION_SECTION_VALUES,
  GUIDING_PRINCIPLES_DIVESTITURES_KEY,
  GUIDING_PRINCIPLES_DIVESTITURES_TITLE,
  GUIDING_PRINCIPLES_TITLE,
  MATURITY_ASSESSMENT_TITLE,
  PERMISSION_OPTION,
} from '@model';
import alert from '@components/alert';
import { v4 as uuidv4 } from 'uuid';

export const processSystemModuleResponse = (
  res,
  addSystemModuleFlag,
  copyInternalPermission,
  instanceType,
) => {
  if (res.customizeModuleTmplInfo) {
    return {
      customizeModule: res.customizeModuleTmplInfo.map(
        ({ internalPermTmplList, clientPermTmplList, ...rest }) => {
          if (rest.value === 'insight' && Array.isArray(internalPermTmplList)) {
            internalPermTmplList = internalPermTmplList.filter(
              (i) => i.value !== 'insights',
            );
          }
          const item = {
            ...rest,
            clientPermissions: clientPermTmplList,
            internalPermissions: internalPermTmplList,
          };
          if (copyInternalPermission) {
            const mapHandler =
              changePermissionLabelByInstanceType(instanceType);
            item.originPermissions = internalPermTmplList
              .map(mapHandler)
              .filter((i) => i?.label);
          }
          if (
            addSystemModuleFlag &&
            ['imageModule', 'powerbiModule'].includes(rest.type)
          ) {
            item.isFromSystemModule = true;
          } else if (addSystemModuleFlag && rest.type === 'textContainer') {
            item.isTemplate = true;
          }
          return item;
        },
      ),
      projectHomeTmplt: res.customizeProjectHomeTmplInfo,
    };
  }

  return null;
};

export const getPermissionOptions = (modules = []) => {
  const allOptions = cloneDeep(PERMISSION_OPTION);
  const result = {};
  modules.forEach((item) => {
    if (item.value && allOptions[item.value]) {
      if (
        [
          DEFALUT_PERMISSION_SECTION.ANALYSIS,
          DEFALUT_PERMISSION_SECTION.INSIGHT,
        ].includes(item.value)
      ) {
        result[item.value] = allOptions[item.value].filter((i) =>
          item.internalPermissions.find(
            (j) => j.display && j.value === i.value,
          ),
        );
        if (!result[item.value].length) {
          Reflect.deleteProperty(result, item.value);
        }
      } else {
        result[item.value] = allOptions[item.value];
      }
    }
  });

  return result;
};

export function getDefaultOptions({
  key,
  moduleName,
  location,
  isCustomizePermission,
  originPermissions = [],
}) {
  const onSystemSetting = location?.pathname.includes('settings/systemModule');
  const isCustomizedProjectType =
    new URLSearchParams(location.search).get('curType') === 'New Type';
  if (isCustomizePermission) {
    return [{ value: key, label: moduleName }];
  }
  let options = onSystemSetting ? PERMISSION_OPTION[key] : originPermissions;
  if (key === DEFALUT_PERMISSION_SECTION.INSIGHT && isCustomizedProjectType) {
    options = options.filter(
      (i) => i.value !== GUIDING_PRINCIPLES_DIVESTITURES_KEY,
    );
  }
  return cloneDeep(options);
}

export const generateSystemModuleRequest = (formData) => {
  if (formData.customizeModule && formData.projectHomeTmplt) {
    return {
      customizeModuleTmplInfo: formData.customizeModule.map(
        ({ clientPermissions, internalPermissions, ...rest }) => ({
          internalPermTmplList: internalPermissions,
          clientPermTmplList: clientPermissions,
          ...rest,
        }),
      ),
      customizeProjectHomeTmplInfo: formData.projectHomeTmplt,
    };
  }
  return null;
};

function changePermissionLabelByInstanceType(instanceType) {
  return (i) => {
    let label = i.label;
    if (
      label === MATURITY_ASSESSMENT_TITLE &&
      ['Divestiture', 'Integration'].includes(instanceType)
    ) {
      label = {
        Divestiture: 'Separation Readiness Assessment',
        Integration: 'Integration Readiness Assessment',
      }[instanceType];
    }
    if (instanceType === 'Customize' && label === GUIDING_PRINCIPLES_TITLE) {
      label = 'Guiding Principles';
    }
    if (
      instanceType === 'Customize' &&
      label === GUIDING_PRINCIPLES_DIVESTITURES_TITLE
    ) {
      label = null;
    }
    return {
      ...i,
      label,
    };
  };
}

export const copyPermissions = (key, instanceType) => {
  const mapHandler = changePermissionLabelByInstanceType(instanceType);
  return cloneDeep(PERMISSION_OPTION[key])
    .map(mapHandler)
    .filter((i) => i?.label);
};

export const fillOriginPermission = (moduleItem, instanceType) => {
  if (
    DEFAULT_PERMISSION_SECTION_VALUES.includes(moduleItem.value) &&
    !moduleItem.originPermissions?.length
  ) {
    moduleItem.originPermissions =
      copyPermissions(moduleItem.value, instanceType) || [];
  }
};

export const validateSubmoduleName = (moduleList) => {
  const subModuleNames = moduleList
    .filter((i) =>
      [
        DEFALUT_PERMISSION_SECTION.INSIGHT,
        DEFALUT_PERMISSION_SECTION.ANALYSIS,
      ].includes(i.value),
    )
    .flatMap((i) => i.originPermissions || [])
    .map((i) => i.label);
  const moduleNames = moduleList.map((i) => i.module);
  // to do: trim the label
  const valid =
    subModuleNames.length === Array.from(new Set(subModuleNames)).length &&
    !moduleNames.some((i) => subModuleNames.includes(i));
  if (!valid) {
    alert.warning('Name already exists');
  }
  return valid;
};

export const addIdToTemplateData = (data) => {
  if (!data) {
    return;
  }
  if (data?.lifecycleSectionInfo?.title) {
    data.lifecycleSectionInfo.sectionTitle = data.lifecycleSectionInfo?.title;
  }
  // eslint-disable-next-line no-unused-expressions
  data?.lifecycleSectionInfo?.lifecyclePeriods?.forEach((i) => {
    if (!i.id) {
      i.id = uuidv4();
    }
    // eslint-disable-next-line no-unused-expressions
    i.lifecycleItems?.forEach((item) => {
      if (!item.id) {
        item.id = uuidv4();
      }
      if (typeof item.richContent === 'string') {
        try {
          item.richContent = JSON.parse(item.richContent);
        } catch (e) {
          console.log(e);
        }
      }
    });
  });
  return data;
};
